// Requests sub-tab for In-Room QR admin panel — single component (no sub-comps).
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw, Sparkles, Check, X, Footprints } from 'lucide-react';
import { apiCall } from './_shared';

const InRoomRequestsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('new');
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const qs = statusFilter ? `?status=${statusFilter}` : '';
      const json = await apiCall(`/api/admin/inroom/requests${qs}`);
      setData(json);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const advance = async (id, s) => {
    try {
      await apiCall(`/api/admin/inroom/requests/${id}`, {
        method: 'PATCH', body: JSON.stringify({ status: s }),
      });
      load();
    } catch (e) { alert(e.message); }
  };

  const items = data?.items || [];
  const c = data?.counts || {};

  const pill = (s) => {
    if (s === 'new') return 'bg-orange-100 text-orange-700';
    if (s === 'accepted') return 'bg-sky-100 text-sky-700';
    if (s === 'done') return 'bg-green-100 text-green-700';
    return 'bg-slate-200 text-slate-500';
  };

  const filterBtns = [
    { k: 'new', label: `New (${c.new || 0})` },
    { k: 'accepted', label: `On the way (${c.accepted || 0})` },
    { k: 'done', label: `Done (${c.done || 0})` },
    { k: '', label: 'All' },
  ].map((f) => (
    <button
      key={f.k}
      onClick={() => setStatusFilter(f.k)}
      className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${statusFilter === f.k ? 'bg-orange-500 text-white border-orange-500' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'}`}
      data-testid={`req-filter-${f.k || 'all'}`}
    >
      {f.label}
    </button>
  ));

  const reqCards = items.map((r, ri) => (
    <div key={r.id || ri} className="bg-white rounded-xl border p-4" data-testid={`req-card-${r.id}`}>
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase tracking-widest text-slate-500 font-bold">
            Room {r.room_id}{r.guest_name ? ` · ${r.guest_name}` : ''}
          </p>
          <p className="text-base font-bold text-slate-900 mt-0.5">{(r.type || '').replace(/_/g, ' ')}</p>
          {r.note && <p className="text-sm text-slate-700 mt-1">{r.note}</p>}
          {r.room_name && (
            <p className="text-xs text-slate-400 mt-1">
              {r.unit_name && <span className="font-bold text-slate-600">Room {r.unit_name} · </span>}
              {r.room_name}
            </p>
          )}
        </div>
        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${pill(r.status)}`}>{r.status === 'accepted' ? `on the way · ${r.accepted_by || ''}` : r.status}</span>
      </div>
      {(r.status === 'new' || r.status === 'accepted') && (
        <div className="mt-3 flex gap-2 flex-wrap">
          {r.status === 'new' && (
            <button onClick={() => advance(r.id, 'accepted')} className="px-3 py-1.5 rounded-full bg-sky-600 hover:bg-sky-700 text-white text-xs font-semibold flex items-center gap-1" data-testid={`req-accept-${r.id}`}>
              <Footprints className="w-3.5 h-3.5" /> Accept — on my way
            </button>
          )}
          <button onClick={() => advance(r.id, 'done')} className="px-3 py-1.5 rounded-full bg-green-500 hover:bg-green-600 text-white text-xs font-semibold flex items-center gap-1" data-testid={`req-done-${r.id}`}>
            <Check className="w-3.5 h-3.5" /> Mark done
          </button>
          <button onClick={() => advance(r.id, 'dismissed')} className="px-3 py-1.5 rounded-full bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 text-xs font-semibold flex items-center gap-1" data-testid={`req-dismiss-${r.id}`}>
            <X className="w-3.5 h-3.5" /> Dismiss
          </button>
        </div>
      )}
    </div>
  ));

  return (
    <div data-testid="inroom-requests-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">{filterBtns}</div>
        <button onClick={load} disabled={loading} className="text-slate-500 hover:text-slate-700 flex items-center gap-1 text-sm">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
        </button>
      </div>
      {err && <p className="text-sm text-red-600 mb-3">{err}</p>}
      {items.length === 0 && !loading ? (
        <div className="text-center py-16 text-slate-400 bg-white rounded-xl border" data-testid="req-empty">
          <Sparkles className="w-10 h-10 mx-auto mb-2" />
          <p className="text-sm">No {statusFilter || ''} guest requests.</p>
        </div>
      ) : (
        <div className="space-y-3">{reqCards}</div>
      )}
    </div>
  );
};

export default InRoomRequestsTab;
